import React, { useEffect, useState } from "react";
import { Star, Quote, Loader2, Check } from "lucide-react";
import { getReviews, submitReview } from "../api";
import { useToast } from "../hooks/use-toast";

const Stars = ({ n, size = 15, onSet }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} onClick={onSet ? () => onSet(i) : undefined}
        className={`${onSet ? "cursor-pointer" : ""} ${i <= n ? "fill-[#b8935f] text-[#b8935f]" : "text-[#d8cdbb]"}`} />
    ))}
  </div>
);

const ReviewsSection = () => {
  const { toast } = useToast();
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "Client", rating: 5, text: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { getReviews().then(setData).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return;
    setBusy(true);
    try { await submitReview(form); setSent(true); toast({ title: "Thank you!", description: "Your review is awaiting moderation." }); }
    catch { toast({ title: "Couldn't submit", description: "Please try again." }); }
    finally { setBusy(false); }
  };

  return (
    <section className="py-24 bg-[#faf7f2]" data-testid="reviews-section">
      <div className="max-w-[1180px] mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <p className="text-[12px] tracking-[0.28em] uppercase text-[#b8935f] mb-3">Client reviews</p>
            <h2 className="font-display text-[34px] md:text-[44px] font-300 text-[#2b2b2b] leading-tight">What our clients say</h2>
            {data.count > 0 && (
              <div className="flex items-center gap-3 mt-3">
                <Stars n={Math.round(data.average)} />
                <span className="text-[14px] text-[#6a6a6a]">{data.average} / 5 · {data.count} reviews</span>
              </div>
            )}
          </div>
          <button onClick={() => setOpen((o) => !o)} data-testid="leave-review-btn" className="h-12 px-7 rounded-full bg-[#1c1c1e] text-white text-[14px] font-500 hover:bg-black transition-colors self-start">Leave a review</button>
        </div>

        {open && (
          <div className="bg-white rounded-2xl border border-[#f0ece5] p-6 mb-10 max-w-[560px]" data-testid="review-form">
            {sent ? (
              <div className="flex items-center gap-2 text-green-600 py-4"><Check size={18} /> Thank you — your review is awaiting moderation.</div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f]" data-testid="review-name" />
                  <input placeholder="e.g. Vendor · Bath" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-11 px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f]" />
                </div>
                <div className="flex items-center gap-3"><span className="text-[13px] text-[#6a6a6a]">Rating</span><Stars n={form.rating} size={22} onSet={(r) => setForm({ ...form, rating: r })} /></div>
                <textarea required rows={4} placeholder="Tell us about your experience…" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f] resize-none" data-testid="review-text" />
                <button type="submit" disabled={busy} className="h-11 px-6 rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] disabled:opacity-60 inline-flex items-center gap-2" data-testid="review-submit">{busy && <Loader2 size={15} className="animate-spin" />} Submit review</button>
              </form>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5">
          {data.reviews.slice(0, 6).map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="review-card">
              <Quote size={22} className="text-[#e2d3bf] mb-3" />
              <p className="text-[14px] text-[#4a4a4a] leading-relaxed mb-4">“{r.text}”</p>
              <div className="flex items-center justify-between">
                <div><p className="text-[14px] font-500 text-[#2b2b2b]">{r.name}</p><p className="text-[12px] text-[#a0a0a0]">{r.role}</p></div>
                <Stars n={r.rating} size={13} />
              </div>
            </div>
          ))}
        </div>
        {data.reviews.length === 0 && <p className="text-[14px] text-[#8a8a8a]">Be the first to leave a review.</p>}
      </div>
    </section>
  );
};

export default ReviewsSection;
